import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, Mail, MessageSquare, Save, CheckCircle, HardDrive, MemoryStick, Network, Shield, Users, Activity, Settings2, Plus, Trash2, Clock, Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

interface AlertRuleConfig {
  id: string;
  name: string;
  description: string;
  severity: 'warning' | 'critical';
  category: string;
  unit: string;
  defaultThreshold: number;
  comparison: '>' | '<' | '==' | '!=';
  icon: React.ReactNode;
}

interface AlertRuleState {
  enabled: boolean;
  threshold: number;
}

interface CustomAlert {
  id: string;
  name: string;
  metric: string;
  comparison: '>' | '<' | '==' | '!=';
  threshold: number;
  severity: 'warning' | 'critical';
  enabled: boolean;
}

interface AlertSettings {
  email_notifications: boolean;
  email_addresses: string[];
  slack_webhook_url: string;
  teams_webhook_url: string;
  alert_rules: Record<string, AlertRuleState>;
  cooldown_minutes: number;
  custom_alerts: CustomAlert[];
}

// All configurable alert rules
const alertRuleConfigs: AlertRuleConfig[] = [
  // Memory
  { id: 'memory_commit_critical', name: 'Memory Commit Critical', description: 'Memory commit percentage', severity: 'critical', category: 'Memory', unit: '%', defaultThreshold: 90, comparison: '>', icon: <MemoryStick className="h-4 w-4" /> },
  { id: 'memory_commit_warning', name: 'Memory Commit Warning', description: 'Memory commit percentage', severity: 'warning', category: 'Memory', unit: '%', defaultThreshold: 80, comparison: '>', icon: <MemoryStick className="h-4 w-4" /> },
  
  // Disk Space
  { id: 'disk_space_c_critical', name: 'System Disk Space Critical', description: 'C: drive free space', severity: 'critical', category: 'Disk', unit: 'GB', defaultThreshold: 10, comparison: '<', icon: <HardDrive className="h-4 w-4" /> },
  { id: 'disk_space_c_warning', name: 'System Disk Space Warning', description: 'C: drive free space', severity: 'warning', category: 'Disk', unit: 'GB', defaultThreshold: 25, comparison: '<', icon: <HardDrive className="h-4 w-4" /> },
  { id: 'disk_space_other_critical', name: 'Data Disk Space Critical', description: 'Non-system disk free space', severity: 'critical', category: 'Disk', unit: 'GB', defaultThreshold: 20, comparison: '<', icon: <HardDrive className="h-4 w-4" /> },
  { id: 'disk_space_other_warning', name: 'Data Disk Space Warning', description: 'Non-system disk free space', severity: 'warning', category: 'Disk', unit: 'GB', defaultThreshold: 50, comparison: '<', icon: <HardDrive className="h-4 w-4" /> },
  
  // Disk Performance
  { id: 'disk_latency_critical', name: 'Disk Latency Critical', description: 'Disk read/write latency', severity: 'critical', category: 'Disk', unit: 'ms', defaultThreshold: 50, comparison: '>', icon: <HardDrive className="h-4 w-4" /> },
  { id: 'disk_latency_warning', name: 'Disk Latency Warning', description: 'Disk read/write latency', severity: 'warning', category: 'Disk', unit: 'ms', defaultThreshold: 20, comparison: '>', icon: <HardDrive className="h-4 w-4" /> },
  
  // Network
  { id: 'network_errors_critical', name: 'Network Errors Critical', description: 'Network errors per minute', severity: 'critical', category: 'Network', unit: '/min', defaultThreshold: 100, comparison: '>', icon: <Network className="h-4 w-4" /> },
  { id: 'network_errors_warning', name: 'Network Errors Warning', description: 'Network errors per minute', severity: 'warning', category: 'Network', unit: '/min', defaultThreshold: 10, comparison: '>', icon: <Network className="h-4 w-4" /> },
  { id: 'tcp_connections_critical', name: 'TCP Connections Critical', description: 'Active TCP connections', severity: 'critical', category: 'Network', unit: '', defaultThreshold: 5000, comparison: '>', icon: <Network className="h-4 w-4" /> },
  { id: 'tcp_connections_warning', name: 'TCP Connections Warning', description: 'Active TCP connections', severity: 'warning', category: 'Network', unit: '', defaultThreshold: 1000, comparison: '>', icon: <Network className="h-4 w-4" /> },
  
  // Security - Logins
  { id: 'failed_logins_critical', name: 'Failed Logins Critical', description: 'Failed logins in 24 hours', severity: 'critical', category: 'Security', unit: '', defaultThreshold: 50, comparison: '>', icon: <Users className="h-4 w-4" /> },
  { id: 'failed_logins_warning', name: 'Failed Logins Warning', description: 'Failed logins in 24 hours', severity: 'warning', category: 'Security', unit: '', defaultThreshold: 10, comparison: '>', icon: <Users className="h-4 w-4" /> },
  
  // Security - Admin Accounts
  { id: 'admin_accounts_critical', name: 'Too Many Admins Critical', description: 'Number of admin accounts', severity: 'critical', category: 'Security', unit: '', defaultThreshold: 10, comparison: '>', icon: <Users className="h-4 w-4" /> },
  { id: 'admin_accounts_warning', name: 'Too Many Admins Warning', description: 'Number of admin accounts', severity: 'warning', category: 'Security', unit: '', defaultThreshold: 5, comparison: '>', icon: <Users className="h-4 w-4" /> },
  
  // Processes
  { id: 'process_memory_critical', name: 'Process Memory Critical', description: 'Single process memory usage', severity: 'critical', category: 'Processes', unit: 'GB', defaultThreshold: 8, comparison: '>', icon: <Activity className="h-4 w-4" /> },
  { id: 'process_memory_warning', name: 'Process Memory Warning', description: 'Single process memory usage', severity: 'warning', category: 'Processes', unit: 'GB', defaultThreshold: 4, comparison: '>', icon: <Activity className="h-4 w-4" /> },
  
  // Server Status
  { id: 'server_offline', name: 'Server Offline', description: 'Minutes since last metrics', severity: 'critical', category: 'Availability', unit: 'min', defaultThreshold: 5, comparison: '>', icon: <Bell className="h-4 w-4" /> },
];

// Toggle-only rules (no threshold)
const toggleOnlyRules = [
  { id: 'defender_inactive', name: 'Windows Defender Inactive', description: 'Alert when Windows Defender is disabled', severity: 'critical' as const, category: 'Security', icon: <Shield className="h-4 w-4" /> },
  { id: 'defender_outdated', name: 'Windows Defender Outdated', description: 'Alert when Defender signatures are outdated', severity: 'warning' as const, category: 'Security', icon: <Shield className="h-4 w-4" /> },
  { id: 'firewall_disabled', name: 'Firewall Disabled', description: 'Alert when Windows Firewall is disabled', severity: 'critical' as const, category: 'Security', icon: <Shield className="h-4 w-4" /> },
  { id: 'bitlocker_disabled', name: 'BitLocker Not Enabled', description: 'Alert when OS volume is not encrypted', severity: 'critical' as const, category: 'Security', icon: <Shield className="h-4 w-4" /> },
  { id: 'secure_boot_disabled', name: 'Secure Boot Disabled', description: 'Alert when Secure Boot is not enabled', severity: 'warning' as const, category: 'Security', icon: <Shield className="h-4 w-4" /> },
  { id: 'tpm_disabled', name: 'TPM Disabled', description: 'Alert when TPM is not enabled', severity: 'warning' as const, category: 'Security', icon: <Shield className="h-4 w-4" /> },
];

// Available metrics for custom alerts
const availableMetrics = [
  { value: 'cpu_usage', label: 'CPU Gebruik', unit: '%' },
  { value: 'memory_usage', label: 'Memory Gebruik', unit: '%' },
  { value: 'memory_committed_percent', label: 'Memory Commit', unit: '%' },
  { value: 'disk_usage', label: 'Disk Gebruik', unit: '%' },
  { value: 'disk_free_space_percent', label: 'Disk Vrije Ruimte', unit: '%' },
  { value: 'network_in_mbps', label: 'Network In', unit: 'Mbps' },
  { value: 'network_out_mbps', label: 'Network Out', unit: 'Mbps' },
  { value: 'network_errors', label: 'Network Errors', unit: '/min' },
  { value: 'process_count', label: 'Aantal Processen', unit: '' },
  { value: 'uptime_seconds', label: 'Uptime', unit: 'sec' },
  { value: 'tcp_established_connections', label: 'TCP Connecties', unit: '' },
  { value: 'failed_login_last_24h', label: 'Mislukte Logins (24u)', unit: '' },
  { value: 'admin_accounts_count', label: 'Admin Accounts', unit: '' },
  { value: 'pending_updates_count', label: 'Pending Updates', unit: '' },
  { value: 'boot_time_seconds', label: 'Boot Time', unit: 'sec' },
  { value: 'disk_read_latency_ms', label: 'Disk Read Latency', unit: 'ms' },
  { value: 'disk_write_latency_ms', label: 'Disk Write Latency', unit: 'ms' },
];

export default function AlertsPage() {
  const { profile } = useAuth();
  const [settings, setSettings] = useState<AlertSettings>({
    email_notifications: true,
    email_addresses: [],
    slack_webhook_url: '',
    teams_webhook_url: '',
    alert_rules: {},
    cooldown_minutes: 15,
    custom_alerts: [],
  });
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSending, setTestingSending] = useState<string | null>(null);
  
  // New custom alert form state
  const [newAlert, setNewAlert] = useState<Omit<CustomAlert, 'id' | 'enabled'>>({
    name: '',
    metric: 'cpu_usage',
    comparison: '>',
    threshold: 80,
    severity: 'warning',
  });

  // Initialize alert rules with default values
  useEffect(() => {
    const defaultRules: Record<string, AlertRuleState> = {};
    alertRuleConfigs.forEach(rule => {
      defaultRules[rule.id] = { enabled: true, threshold: rule.defaultThreshold };
    });
    toggleOnlyRules.forEach(rule => {
      defaultRules[rule.id] = { enabled: true, threshold: 0 };
    });
    setSettings(prev => ({
      ...prev,
      alert_rules: { ...defaultRules, ...prev.alert_rules }
    }));
  }, []);

  useEffect(() => {
    if (profile?.tenant_id) {
      fetchAlertSettings();
    }
  }, [profile]);

  const fetchAlertSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('alert_settings')
        .select('*')
        .eq('tenant_id', profile.tenant_id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        const defaultRules: Record<string, AlertRuleState> = {};
        alertRuleConfigs.forEach(rule => {
          defaultRules[rule.id] = { enabled: true, threshold: rule.defaultThreshold };
        });
        toggleOnlyRules.forEach(rule => {
          defaultRules[rule.id] = { enabled: true, threshold: 0 };
        });
        
        // Merge saved rules with defaults (supports legacy boolean format)
        const savedRules = (data as any).alert_rules || {};
        Object.keys(savedRules).forEach((key) => {
          if (!defaultRules[key]) return;
          const raw = savedRules[key];

          // Legacy format: { [ruleId]: boolean }
          if (typeof raw === 'boolean') {
            defaultRules[key] = { ...defaultRules[key], enabled: raw };
            return;
          }

          // New format: { [ruleId]: { enabled, threshold } }
          if (raw && typeof raw === 'object') {
            defaultRules[key] = {
              enabled: typeof raw.enabled === 'boolean' ? raw.enabled : defaultRules[key].enabled,
              threshold: typeof raw.threshold === 'number' ? raw.threshold : defaultRules[key].threshold,
            };
          }
        });
        
        setSettings({
          email_notifications: data.email_notifications,
          email_addresses: data.email_addresses || [],
          slack_webhook_url: data.slack_webhook_url || '',
          teams_webhook_url: (data as any).teams_webhook_url || '',
          alert_rules: defaultRules,
          cooldown_minutes: (data as any).cooldown_minutes ?? 15,
          custom_alerts: (data as any).custom_alerts || [],
        });
      }
    } catch (error) {
      console.error('Error fetching alert settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('alert_settings')
        .upsert({
          tenant_id: profile.tenant_id,
          email_notifications: settings.email_notifications,
          email_addresses: settings.email_addresses,
          slack_webhook_url: settings.slack_webhook_url || null,
          teams_webhook_url: settings.teams_webhook_url || null,
          alert_rules: settings.alert_rules,
          cooldown_minutes: settings.cooldown_minutes,
          custom_alerts: settings.custom_alerts,
        } as any, {
          onConflict: 'tenant_id'
        });

      if (error) throw error;

      toast({
        title: "Instellingen opgeslagen",
        description: "Alert instellingen zijn succesvol bijgewerkt.",
      });
    } catch (error) {
      console.error('Error saving alert settings:', error);
      toast({
        title: "Fout",
        description: "Kon instellingen niet opslaan. Probeer opnieuw.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const addEmail = () => {
    if (emailInput && emailInput.includes('@') && !settings.email_addresses.includes(emailInput)) {
      setSettings(prev => ({
        ...prev,
        email_addresses: [...prev.email_addresses, emailInput]
      }));
      setEmailInput('');
    }
  };

  const removeEmail = (email: string) => {
    setSettings(prev => ({
      ...prev,
      email_addresses: prev.email_addresses.filter(e => e !== email)
    }));
  };

  const toggleAlertRule = (ruleId: string) => {
    setSettings(prev => ({
      ...prev,
      alert_rules: {
        ...prev.alert_rules,
        [ruleId]: {
          ...prev.alert_rules[ruleId],
          enabled: !prev.alert_rules[ruleId]?.enabled
        }
      }
    }));
  };

  const updateThreshold = (ruleId: string, value: number) => {
    setSettings(prev => ({
      ...prev,
      alert_rules: {
        ...prev.alert_rules,
        [ruleId]: {
          ...prev.alert_rules[ruleId],
          threshold: value
        }
      }
    }));
  };

  const resetToDefaults = () => {
    const defaultRules: Record<string, AlertRuleState> = {};
    alertRuleConfigs.forEach(rule => {
      defaultRules[rule.id] = { enabled: true, threshold: rule.defaultThreshold };
    });
    toggleOnlyRules.forEach(rule => {
      defaultRules[rule.id] = { enabled: true, threshold: 0 };
    });
    setSettings(prev => ({
      ...prev,
      alert_rules: defaultRules,
      cooldown_minutes: 15,
    }));
    toast({
      title: "Standaardwaarden hersteld",
      description: "Alle thresholds zijn teruggezet naar de standaardwaarden.",
    });
  };

  const addCustomAlert = () => {
    if (!newAlert.name.trim()) {
      toast({
        title: "Naam vereist",
        description: "Geef een naam op voor de custom alert.",
        variant: "destructive",
      });
      return;
    }
    
    const customAlert: CustomAlert = {
      ...newAlert,
      id: `custom_${Date.now()}`,
      enabled: true,
    };
    
    setSettings(prev => ({
      ...prev,
      custom_alerts: [...prev.custom_alerts, customAlert],
    }));
    
    setNewAlert({
      name: '',
      metric: 'cpu_usage',
      comparison: '>',
      threshold: 80,
      severity: 'warning',
    });
    
    toast({
      title: "Custom alert toegevoegd",
      description: `Alert "${customAlert.name}" is aangemaakt.`,
    });
  };

  const removeCustomAlert = (id: string) => {
    setSettings(prev => ({
      ...prev,
      custom_alerts: prev.custom_alerts.filter(a => a.id !== id),
    }));
  };

  const toggleCustomAlert = (id: string) => {
    setSettings(prev => ({
      ...prev,
      custom_alerts: prev.custom_alerts.map(a => 
        a.id === id ? { ...a, enabled: !a.enabled } : a
      ),
    }));
  };

  const sendTestAlert = async (type: 'slack' | 'teams' | 'email') => {
    setTestingSending(type);
    try {
      const { error } = await supabase.functions.invoke('send-alert', {
        body: {
          type,
          recipients: type === 'email' ? settings.email_addresses : undefined,
          webhook_url: type === 'slack' ? settings.slack_webhook_url : type === 'teams' ? settings.teams_webhook_url : undefined,
          message: `🧪 Test alert from ServSpect - ${new Date().toLocaleString()}`,
          hostname: 'Test Server',
          severity: 'info'
        }
      });

      if (error) throw error;

      toast({
        title: "Test verzonden",
        description: `Test alert is verzonden naar ${type === 'email' ? 'email' : type === 'slack' ? 'Slack' : 'Teams'}.`,
      });
    } catch (error) {
      console.error('Error sending test alert:', error);
      toast({
        title: "Fout",
        description: `Kon test alert niet verzenden. Controleer je ${type} configuratie.`,
        variant: "destructive",
      });
    } finally {
      setTestingSending(null);
    }
  };

  // Group rules by category
  const rulesByCategory = alertRuleConfigs.reduce((acc, rule) => {
    if (!acc[rule.category]) acc[rule.category] = [];
    acc[rule.category].push(rule);
    return acc;
  }, {} as Record<string, AlertRuleConfig[]>);

  // Add toggle-only rules to their categories
  toggleOnlyRules.forEach(rule => {
    if (!rulesByCategory[rule.category]) rulesByCategory[rule.category] = [];
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Alerts & Notificaties</h1>
        <div className="animate-pulse space-y-4">
          <Card>
            <CardHeader>
              <div className="h-6 bg-muted rounded w-48"></div>
              <div className="h-4 bg-muted rounded w-64"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="h-10 bg-muted rounded"></div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Alerts & Notificaties</h1>
          <p className="text-muted-foreground">
            Configureer hoe je op de hoogte wilt worden gehouden van server problemen
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving} className="shadow-md hover:shadow-lg transition-all duration-200">
          <Save className="mr-2 h-4 w-4" />
          {saving ? "Opslaan..." : "Instellingen Opslaan"}
        </Button>
      </div>

      <Tabs defaultValue="notifications" className="w-full">
        <TabsList>
          <TabsTrigger value="notifications">Notificatie Kanalen</TabsTrigger>
          <TabsTrigger value="rules">Alert Regels</TabsTrigger>
          <TabsTrigger value="custom">Custom Alerts</TabsTrigger>
          <TabsTrigger value="settings">Instellingen</TabsTrigger>
        </TabsList>

        <TabsContent value="notifications" className="space-y-6">
          {/* Email Notifications */}
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Notificaties
              </CardTitle>
              <CardDescription>
                Configureer email alerts voor je team
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="email-notifications">Email Notificaties Inschakelen</Label>
                  <p className="text-sm text-muted-foreground">
                    Ontvang alerts via email wanneer er problemen zijn
                  </p>
                </div>
                <Switch
                  id="email-notifications"
                  checked={settings.email_notifications}
                  onCheckedChange={(checked) => 
                    setSettings(prev => ({ ...prev, email_notifications: checked }))
                  }
                />
              </div>

              {settings.email_notifications && (
                <div className="space-y-4">
                  <div>
                    <Label>Email Adressen</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        placeholder="team@company.com"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                      />
                      <Button onClick={addEmail} type="button">Toevoegen</Button>
                    </div>
                  </div>

                  {settings.email_addresses.length > 0 && (
                    <div className="space-y-2">
                      <Label>Ontvangers</Label>
                      <div className="flex flex-wrap gap-2">
                        {settings.email_addresses.map((email) => (
                          <Badge
                            key={email}
                            variant="secondary"
                            className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                            onClick={() => removeEmail(email)}
                          >
                            {email} ×
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {settings.email_addresses.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => sendTestAlert('email')}
                      disabled={testingSending === 'email'}
                    >
                      {testingSending === 'email' ? 'Verzenden...' : 'Test Email Verzenden'}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Slack Integration */}
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Slack Integratie
              </CardTitle>
              <CardDescription>
                Verstuur alerts naar je Slack kanalen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 relative z-10">
              <div>
                <Label htmlFor="slack-webhook">Slack Webhook URL</Label>
                <Input
                  id="slack-webhook"
                  placeholder="https://hooks.slack.com/services/..."
                  value={settings.slack_webhook_url}
                  onChange={(e) => 
                    setSettings(prev => ({ ...prev, slack_webhook_url: e.target.value }))
                  }
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Maak een webhook URL aan in je Slack workspace instellingen
                </p>
              </div>

              {settings.slack_webhook_url && (
                <>
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Slack integratie geconfigureerd.
                    </AlertDescription>
                  </Alert>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => sendTestAlert('slack')}
                    disabled={testingSending === 'slack'}
                  >
                    {testingSending === 'slack' ? 'Verzenden...' : 'Test Slack Bericht Verzenden'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Microsoft Teams Integration */}
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary))]/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-2">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.35 8.35a2.5 2.5 0 1 0-3.45-3.45 4 4 0 0 0-7.8 0A2.5 2.5 0 1 0 4.65 8.35 4 4 0 0 0 7 16h10a4 4 0 0 0 2.35-7.65zM17 14H7a2 2 0 0 1 0-4h.09A4 4 0 0 1 7 9a2 2 0 0 1 4 0 2 2 0 0 1 4 0 2 2 0 0 1-.09 1H17a2 2 0 0 1 0 4z"/>
                </svg>
                Microsoft Teams Integratie
              </CardTitle>
              <CardDescription>
                Verstuur alerts naar je Teams kanalen
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 relative z-10">
              <div>
                <Label htmlFor="teams-webhook">Teams Webhook URL</Label>
                <Input
                  id="teams-webhook"
                  placeholder="https://outlook.office.com/webhook/..."
                  value={settings.teams_webhook_url}
                  onChange={(e) => 
                    setSettings(prev => ({ ...prev, teams_webhook_url: e.target.value }))
                  }
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Maak een Incoming Webhook connector aan in je Teams kanaal
                </p>
              </div>

              {settings.teams_webhook_url && (
                <>
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Teams integratie geconfigureerd.
                    </AlertDescription>
                  </Alert>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => sendTestAlert('teams')}
                    disabled={testingSending === 'teams'}
                  >
                    {testingSending === 'teams' ? 'Verzenden...' : 'Test Teams Bericht Verzenden'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules">
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings2 className="h-5 w-5" />
                    Alert Regels Configureren
                  </CardTitle>
                  <CardDescription>
                    Pas thresholds aan naar je eigen wensen
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={resetToDefaults}>
                  Reset naar standaard
                </Button>
              </div>
            </CardHeader>
            <CardContent className="relative z-10">
              <div className="space-y-8">
                {Object.entries(rulesByCategory).map(([category, rules]) => (
                  <div key={category}>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2 uppercase tracking-wider">
                      {rules[0]?.icon}
                      {category}
                    </h3>
                    <div className="space-y-3">
                      {rules.map((rule) => {
                        const ruleState = settings.alert_rules[rule.id] || { enabled: true, threshold: rule.defaultThreshold };
                        return (
                          <div 
                            key={rule.id} 
                            className={`flex items-center gap-4 p-4 border rounded-lg transition-all ${ruleState.enabled ? 'bg-card' : 'bg-muted/30 opacity-60'}`}
                          >
                            <Switch 
                              checked={ruleState.enabled}
                              onCheckedChange={() => toggleAlertRule(rule.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm">{rule.name}</span>
                                <Badge 
                                  variant={rule.severity === 'critical' ? 'destructive' : 'default'}
                                  className={`text-xs ${rule.severity === 'warning' ? 'bg-warning text-warning-foreground' : ''}`}
                                >
                                  {rule.severity}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{rule.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{rule.comparison}</span>
                              <Input
                                type="number"
                                value={ruleState.threshold}
                                onChange={(e) => updateThreshold(rule.id, parseFloat(e.target.value) || 0)}
                                disabled={!ruleState.enabled}
                                className="w-20 h-8 text-sm text-center"
                              />
                              <span className="text-xs text-muted-foreground w-8">{rule.unit}</span>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Toggle-only rules for this category */}
                      {toggleOnlyRules.filter(r => r.category === category).map((rule) => {
                        const ruleState = settings.alert_rules[rule.id] || { enabled: true, threshold: 0 };
                        return (
                          <div 
                            key={rule.id} 
                            className={`flex items-center gap-4 p-4 border rounded-lg transition-all ${ruleState.enabled ? 'bg-card' : 'bg-muted/30 opacity-60'}`}
                          >
                            <Switch 
                              checked={ruleState.enabled}
                              onCheckedChange={() => toggleAlertRule(rule.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm">{rule.name}</span>
                                <Badge 
                                  variant={rule.severity === 'critical' ? 'destructive' : 'default'}
                                  className={`text-xs ${rule.severity === 'warning' ? 'bg-warning text-warning-foreground' : ''}`}
                                >
                                  {rule.severity}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{rule.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground italic">Aan/Uit</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Alerts Tab */}
        <TabsContent value="custom" className="space-y-6">
          {/* Create New Custom Alert */}
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Nieuwe Custom Alert
              </CardTitle>
              <CardDescription>
                Maak een eigen alert regel aan op basis van een metric
              </CardDescription>
            </CardHeader>
            <CardContent className="relative z-10">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
                <div className="lg:col-span-2">
                  <Label>Naam</Label>
                  <Input
                    placeholder="Bijv. Hoge CPU Server"
                    value={newAlert.name}
                    onChange={(e) => setNewAlert(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Metric</Label>
                  <Select
                    value={newAlert.metric}
                    onValueChange={(value) => setNewAlert(prev => ({ ...prev, metric: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMetrics.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Conditie</Label>
                  <Select
                    value={newAlert.comparison}
                    onValueChange={(value: '>' | '<' | '==' | '!=') => setNewAlert(prev => ({ ...prev, comparison: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value=">">Groter dan (&gt;)</SelectItem>
                      <SelectItem value="<">Kleiner dan (&lt;)</SelectItem>
                      <SelectItem value="==">Gelijk aan (==)</SelectItem>
                      <SelectItem value="!=">Niet gelijk aan (!=)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Threshold</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={newAlert.threshold}
                      onChange={(e) => setNewAlert(prev => ({ ...prev, threshold: parseFloat(e.target.value) || 0 }))}
                    />
                    <span className="text-xs text-muted-foreground">
                      {availableMetrics.find(m => m.value === newAlert.metric)?.unit}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label>Severity</Label>
                    <Select
                      value={newAlert.severity}
                      onValueChange={(value: 'warning' | 'critical') => setNewAlert(prev => ({ ...prev, severity: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={addCustomAlert} className="mt-auto">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Custom Alerts List */}
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle>Jouw Custom Alerts</CardTitle>
              <CardDescription>
                Beheer je aangepaste alert regels
              </CardDescription>
            </CardHeader>
            <CardContent className="relative z-10">
              {settings.custom_alerts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Nog geen custom alerts aangemaakt</p>
                  <p className="text-sm">Maak hierboven je eerste custom alert aan</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {settings.custom_alerts.map((alert) => {
                    const metric = availableMetrics.find(m => m.value === alert.metric);
                    return (
                      <div 
                        key={alert.id} 
                        className={`flex items-center gap-4 p-4 border rounded-lg transition-all ${alert.enabled ? 'bg-card' : 'bg-muted/30 opacity-60'}`}
                      >
                        <Switch 
                          checked={alert.enabled}
                          onCheckedChange={() => toggleCustomAlert(alert.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{alert.name}</span>
                            <Badge 
                              variant={alert.severity === 'critical' ? 'destructive' : 'default'}
                              className={`text-xs ${alert.severity === 'warning' ? 'bg-warning text-warning-foreground' : ''}`}
                            >
                              {alert.severity}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {metric?.label} {alert.comparison} {alert.threshold}{metric?.unit}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => removeCustomAlert(alert.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card className="relative overflow-hidden" style={{ boxShadow: 'var(--shadow-3d)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Alert Cooldown
              </CardTitle>
              <CardDescription>
                Configureer hoe lang dezelfde alert niet opnieuw verstuurd wordt
              </CardDescription>
            </CardHeader>
            <CardContent className="relative z-10 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label htmlFor="cooldown">Cooldown Periode</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      id="cooldown"
                      type="number"
                      min={1}
                      max={1440}
                      value={settings.cooldown_minutes}
                      onChange={(e) => setSettings(prev => ({ ...prev, cooldown_minutes: parseInt(e.target.value) || 15 }))}
                      className="w-24"
                    />
                    <span className="text-sm text-muted-foreground">minuten</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Na het verzenden van een alert wordt dezelfde alert pas opnieuw verstuurd na deze periode. Dit voorkomt spam bij aanhoudende problemen.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
              
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Hoe werkt de cooldown?</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li>Elke unieke alert (server + regel combinatie) heeft een eigen cooldown</li>
                    <li>Tijdens de cooldown wordt dezelfde alert niet opnieuw verzonden</li>
                    <li>Nieuwe alerts (andere regels of servers) worden direct verstuurd</li>
                    <li>Standaard: 15 minuten - verhoog voor minder notificaties, verlaag voor snellere updates</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSettings(prev => ({ ...prev, cooldown_minutes: 5 }))}
                >
                  5 min
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSettings(prev => ({ ...prev, cooldown_minutes: 15 }))}
                >
                  15 min
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSettings(prev => ({ ...prev, cooldown_minutes: 30 }))}
                >
                  30 min
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSettings(prev => ({ ...prev, cooldown_minutes: 60 }))}
                >
                  1 uur
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
